import React from "react";
import { Box, Typography } from "@mui/material";
import Popper from "./Popper";
import VisualAcuity from "./VisualAcuity";
import AutoRefraction from "./AutoRefraction";
import Refraction from "./Refraction";
import SubjectiveRefraction from "./SubjectiveRefraction";
import FieldOfVision from "./FieldOfVision";
import ColorVision from "./ColorVision";
import AnteriorSegment from "./AnteriorSegment";
import PosteriorSegment from "./PosteriorSegment";
import GlassPrescription from "./GlassPrescription";
import LensPrescription from "./LensPrescription";
import { Container } from "./Container";
import { initialState } from "./initialState";
import useAssessmentLabel from "../../../hooks/useAssessmentLabel";
import { useEyeComponentCheckStatus } from "../../../hooks/useEyeComponentCheckStatus";

// @ts-expect-error - css
import styles from "../Assessment.module.css";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function hasValue(test: any) {
  if (!test) return false;
  return Object.keys(test).some((row) => {
    if (!test[row]) return false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rowObj = test[row] as Record<string, any>;
    const containsValue = Object.keys(rowObj).some((col) => Boolean(rowObj[col]));
    return containsValue;
  });
}

interface Props {
  assessmentEyeExamination: typeof initialState;
  onChange: (v: typeof initialState) => void;
}

const EyeExamination = ({ assessmentEyeExamination, onChange }: Props): React.JSX.Element => {
  const assessmentLabels = useAssessmentLabel();

  const eyeAssessmentData =
    assessmentEyeExamination && Object.keys(assessmentEyeExamination).length > 0
      ? assessmentEyeExamination
      : null;

  const [state, setState] = React.useState(eyeAssessmentData || initialState);

  const {
    vision: visionData,
    anteriorSegment: anteriorSegData,
    posteriorSegment: posteriorSegData,
    glassPrescription: glassPrescriptionData,
    lensPrescription: lensPrescriptionData
  } = state;

  const showAnteriorSegment = hasValue(anteriorSegData);
  const showPosteriorSegment = hasValue(posteriorSegData);
  const showGlassPrescription = hasValue(glassPrescriptionData);
  const showLensPrescription = hasValue(lensPrescriptionData);

  const showVisualAcuity =
    hasValue(visionData?.aided) ||
    hasValue(visionData?.unAided) ||
    hasValue(visionData?.pinHole) ||
    hasValue(visionData?.bestCorrectiveVisualAcuity);

  const showAutoRefraction = hasValue(visionData?.autoRefraction);
  const showRefraction = hasValue(visionData?.refraction);
  const showSubjectiveRefraction = hasValue(visionData?.subjectiveRefraction);
  const showFieldOfVision = hasValue(visionData?.fieldOfVision);
  const showColorVision = hasValue(visionData?.colorVision);

  const { checkStatus } = useEyeComponentCheckStatus();

  React.useEffect(() => {
    if (eyeAssessmentData) {
      setState(eyeAssessmentData);
    }
  }, [eyeAssessmentData]);

  return (
    <Box className={styles.assessmentRow}>
      <Box className={styles.assessmentLabel}>
        <Typography component="span" fontWeight="600">
          {assessmentLabels.eye || "Eye Examination"}
        </Typography>
      </Box>

      <Box
        className={styles.assessmentField}
        sx={{ display: "flex", flexDirection: "column", gap: 3 }}
      >
        {/* Popper */}
        <Popper />

        <Container title="Vision">
          {(checkStatus.vision.visualAcuity || showVisualAcuity) && (
            <VisualAcuity
              visionData={state}
              onVisionChange={(updatedState) => {
                setState(updatedState);
                onChange(updatedState);
              }}
            />
          )}

          {(checkStatus.vision.autoRefraction || showAutoRefraction) && (
            <AutoRefraction
              visionData={state}
              onVisionChange={(updatedState) => {
                setState(updatedState);
                onChange(updatedState);
              }}
            />
          )}

          {(checkStatus.vision.refraction || showRefraction) && (
            <Refraction
              visionData={state}
              onVisionChange={(updatedState) => {
                setState(updatedState);
                onChange(updatedState);
              }}
            />
          )}

          {(checkStatus.vision.subjectiveRefraction || showSubjectiveRefraction) && (
            <SubjectiveRefraction
              visionData={state}
              onVisionChange={(updatedState) => {
                setState(updatedState);
                onChange(updatedState);
              }}
            />
          )}
          {(checkStatus.vision.fieldOfVision || showFieldOfVision) && (
            <FieldOfVision
              visionData={state}
              onVisionChange={(updatedState) => {
                setState(updatedState);
                onChange(updatedState);
              }}
            />
          )}
          {(checkStatus.vision.colorVision || showColorVision) && (
            <ColorVision
              visionData={state}
              onVisionChange={(updatedState) => {
                setState(updatedState);
                onChange(updatedState);
              }}
            />
          )}
        </Container>

        {(checkStatus.anteriorSegment || showAnteriorSegment) && (
          <Container title="Anterior Segment">
            <AnteriorSegment
              segmentData={state}
              onSegmentChange={(updatedState) => {
                setState(updatedState);
                onChange(updatedState);
              }}
            />
          </Container>
        )}

        {(checkStatus.posteriorSegment || showPosteriorSegment) && (
          <Container title="Posterior Segment">
            <PosteriorSegment
              segmentData={state}
              onSegmentChange={(updatedState) => {
                setState(updatedState);
                onChange(updatedState);
              }}
            />
          </Container>
        )}

        {/* Glass Prescription */}
        {(checkStatus.glassPrescription || showGlassPrescription) && (
          <Container title="Glass Prescription">
            <GlassPrescription
              visionData={state}
              onVisionChange={(updatedState) => {
                setState(updatedState);
                onChange(updatedState);
              }}
            />
          </Container>
        )}

        {/* Lens Prescription */}
        {(checkStatus.lensPrescription || showLensPrescription) && (
          <Container title="Lens Prescription">
            <LensPrescription
              visionData={state}
              onVisionChange={(updatedState) => {
                setState(updatedState);
                onChange(updatedState);
              }}
            />
          </Container>
        )}
      </Box>
    </Box>
  );
};

export default EyeExamination;
